import {
    cancelSelectEvent,
    changeSymbolForWidget,
    getCSSStyles,
    getHeaderPinButtonHTML,
    getHeaderPinStateClass,
    getSymbolDropdown,
    getTraderSMCardHTML,
    getWidgetDataURI,
    normalizeWidgetTheme,
    setupHeaderPin,
    toggleWidgetTheme
} from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

interface RiskAssessmentData {
    symbol: string;
    value: number;
    benchmark: number;
}

const RISK_ZONE_COLORS: [number, string][] = [
    [0.4, '#34E36F'],
    [0.6, '#FFD700'],
    [0.8, '#FF895A'],
    [1, '#FF1111']
];

const DUMMY_RISK_DATA: Record<string, Omit<RiskAssessmentData, 'symbol'>> = {
    AAPL: { value: 91, benchmark: 65 },
    MSFT: { value: 38, benchmark: 52 },
    GOOGL: { value: 55, benchmark: 48 },
    AMZN: { value: 72, benchmark: 60 },
    NVDA: { value: 84, benchmark: 70 },
    META: { value: 47, benchmark: 58 },
    TSLA: { value: 63, benchmark: 55 },
    AVGO: { value: 29, benchmark: 44 },
    PEP: { value: 41, benchmark: 50 },
    COST: { value: 33, benchmark: 46 }
};

function getRiskLevel(value: number): { label: string; color: string } {
    if (value < 40) return { label: 'Low', color: '#34E36F' };
    if (value < 60) return { label: 'Medium', color: '#FFD700' };
    if (value < 80) return { label: 'Moderate', color: '#FF895A' };
    return { label: 'High', color: '#FF1111' };
}

export class TraderverseESGCompanyRiskAssessmentRange {
    private container: HTMLElement;
    private config: WidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private riskData: RiskAssessmentData | null = null;
    private currentChart: any;
    private widgetResizeObserver: ResizeObserver | null = null;

    constructor(config: WidgetConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);
        this.shadowRoot = (this.container as any).shadowRoot || this.container.attachShadow({ mode: 'closed' });
        this.init();
        this.widgetResizeObserver = new ResizeObserver(() => this.updateDisplay());
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private fetchSymbols(): Array<{ value: string; label: string }> {
        return Object.keys(DUMMY_RISK_DATA).map((symbol) => ({ label: symbol, value: symbol }));
    }

    private getDefaultConfig(): Partial<WidgetConfig> {
        const symbols = this.fetchSymbols();
        return {
            headerPinned: true,
            symbol: symbols[0].value,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.attachStyles();
        this.loadDummyData(this.config.symbol!);
        if (widget) widget.classList.remove('loading');
        cancelSelectEvent(this.shadowRoot);
        this.emit('ready', {});
        this.config.onReady?.();
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
        <div style="width: ${typeof this.config.width == 'number' ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == 'number' ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
            <div class="headTop">
                <h4 class="title market">${this.getTitleIconSvg()} ESG Company Risk Assessment Range</h4>
                <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<i class="pi pi-ellipsis-v"></i></div>
            </div>
            <div class="inBody">
                <div class="optionsRow">
                    ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
                        <div class="trader-sm-card" id="selected-symbol">
                            ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: 'stock', name: '' })}
                        </div>
                        <span id="symbol-dropdown-placeholder"></span>
                    </div>`}
                </div>
                <div class="chartDv esgRiskGaugeMain" id="esgRiskGaugeContainer">
                    <div id="esgRiskGauge" class="chart coverDv esg-risk-gauge-chart" data-testing-id="esg-risk-assessment-gauge"></div>
                </div>
                <style>
                    .esgRiskGaugeMain {
                        flex: 1;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        padding: 8px 16px 20px;
                        min-height: 280px;
                        overflow: visible;
                    }
                    .esg-risk-gauge-chart {
                        width: 100% !important;
                        min-height: 260px;
                        height: 100%;
                        overflow: visible;
                    }
                </style>
            </div>
        </div>`;
        this.shadowRoot.innerHTML = widgetHtml;
        setupHeaderPin(this.shadowRoot, this.config);

        if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
            const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
            const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
            if (placeholder && dropdown) placeholder.replaceWith(dropdown);
        }
    }

    onSelectChange(stock: any) {
        this.changeSymbol(stock.symbol, stock);
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
            script.async = true;
            script.onload = () => resolve(true);
            this.shadowRoot.appendChild(script);
        });
    }

    private loadDummyData(symbol: string): void {
        const entry = DUMMY_RISK_DATA[symbol] ?? DUMMY_RISK_DATA.AAPL;
        this.riskData = { symbol, ...entry };
        this.renderChart();
        this.emit('dataUpdate', this.riskData);
    }

    private updateDisplay(): void {
        if (!this.riskData || !(window as any).echarts) return;
        this.renderChart();
    }

    private renderChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#esgRiskGauge') as HTMLElement;
        if (!echarts || !container || !this.riskData) return;

        const instance = echarts.getInstanceByDom(container);
        if (instance) echarts.dispose(instance);

        const { value } = this.riskData;
        const risk = getRiskLevel(value);
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const labelColor = getComputedStyle(widgetBox).getPropertyValue('--text-gray') || '#9F9FA2';
        const pointerColor = this.config.theme === 'white' ? '#333' : '#fff';

        const gaugeCenter: [string, string] = ['50%', '58%'];
        const axisLabelStyle = {
            show: true,
            distance: 40,
            color: labelColor.trim() || '#9F9FA2',
            fontSize: 12,
            fontWeight: 500,
            formatter: (val: number) => ([0, 20, 40, 60, 80, 100].includes(val) ? String(val) : '')
        };

        const option = {
            animation: false,
            series: [
                {
                    type: 'gauge',
                    z: 1,
                    startAngle: 180,
                    endAngle: 0,
                    min: 0,
                    max: 100,
                    splitNumber: 5,
                    center: gaugeCenter,
                    radius: '88%',
                    axisLine: {
                        lineStyle: { width: 16, color: RISK_ZONE_COLORS }
                    },
                    pointer: { show: false },
                    axisTick: { show: false },
                    splitLine: { show: false },
                    axisLabel: axisLabelStyle,
                    detail: { show: false },
                    data: [{ value: 0 }]
                },
                {
                    type: 'gauge',
                    z: 1,
                    startAngle: 180,
                    endAngle: 0,
                    min: 0,
                    max: 100,
                    center: gaugeCenter,
                    radius: '62%',
                    axisLine: {
                        lineStyle: { width: 6, color: RISK_ZONE_COLORS }
                    },
                    pointer: { show: false },
                    axisTick: { show: false },
                    splitLine: { show: false },
                    axisLabel: { show: false },
                    detail: { show: false },
                    data: [{ value: 0 }]
                },
                {
                    type: 'gauge',
                    z: 4,
                    startAngle: 180,
                    endAngle: 0,
                    min: 0,
                    max: 100,
                    center: gaugeCenter,
                    radius: '88%',
                    axisLine: { show: false },
                    pointer: {
                        show: true,
                        icon: 'path://M2,0 L-2,0 L0,-80 Z',
                        length: '72%',
                        width: 3,
                        itemStyle: { color: pointerColor }
                    },
                    axisTick: { show: false },
                    splitLine: { show: false },
                    axisLabel: { show: false },
                    anchor: {
                        show: true,
                        showAbove: true,
                        size: 10,
                        itemStyle: {
                            color: this.config.theme === 'white' ? '#ffffff' : '#12121f',
                            borderColor: pointerColor,
                            borderWidth: 2
                        }
                    },
                    detail: {
                        offsetCenter: [0, '28%'],
                        formatter: `{value|${value}%}\n{label|${risk.label}}`,
                        rich: {
                            value: {
                                fontSize: 30,
                                fontWeight: 'bold',
                                color: risk.color,
                                lineHeight: 36
                            },
                            label: {
                                fontSize: 13,
                                color: labelColor.trim() || '#9F9FA2',
                                lineHeight: 20
                            }
                        }
                    },
                    data: [{ value }]
                }
            ]
        };

        this.currentChart = echarts.init(container);
        this.currentChart.setOption(option);
        this.currentChart.resize();
    }

    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(
            this.shadowRoot,
            this.config,
            stock,
            stockId,
            (symbol) => this.loadDummyData(symbol),
            (type, data) => this.emit(type, data)
        );
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }

    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter((h) => h !== handler));
    }

    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite'): void {
        if (!this.shadowRoot) return;
        this.config.theme = toggleWidgetTheme(this.shadowRoot, theme) as 'white' | 'black' | 'default-dark' | 'default-lite';
        this.updateDisplay();
    }

    public destroy(): void {
        this.widgetResizeObserver?.disconnect();
        if (this.currentChart) {
            this.currentChart.dispose();
            this.currentChart = null;
        }
        this.eventHandlers.clear();
        this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) {
            const evt: WidgetEvent = { type, data };
            handlers.forEach((h) => h(evt));
        }
    }

    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }
}

declare global {
    interface Window {
        TraderverseESGCompanyRiskAssessmentRange: typeof TraderverseESGCompanyRiskAssessmentRange;
    }
}
if (typeof window !== 'undefined') {
    window.TraderverseESGCompanyRiskAssessmentRange = TraderverseESGCompanyRiskAssessmentRange;
}
